import { Vector4 } from '@babylonjs/core/Maths/math.vector';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';

/**
 * The room name, painted directly onto the north wall's own top beam
 * (`wall-north-frame-top`, built by `SceneRoot.buildRoom`) rather than mounted as a separate
 * plate standing proud of it. Owner, after seeing the previous free-standing plaque in a
 * headset: "for the plaque, it would be better to just write the room name directly on the
 * white beam. A small black surface on the white beam with white letters really doesn't work
 * that well." (`plan/glass-room-tuning.md`.) The plate mesh, its own `TransformNode` root, and
 * the whole "mount proud of the beam by a clear standoff" geometry this file used to own are
 * gone outright — there is no longer a second mesh at all, only a dedicated material on the
 * beam `SceneRoot` already builds.
 *
 * This file keeps its old name and its old directory (`src/room/`) even though there is no
 * longer a "plaque" object — the room-name-display *concern* still lives here; only its
 * mechanism changed, from a mesh to a texture painted onto someone else's mesh.
 *
 * **Why this needs its own material, not just a different colour on the shared one.** A
 * tempting shortcut is a flush plane coloured to match the beam so it "disappears" into it —
 * exactly the LabelledPlate.ts-style unlit construction (`diffuseTexture`/`emissiveTexture` both
 * the same texture, `disableLighting = true`) every other text-bearing surface in this project
 * uses. Do not: the beam is a normal, *lit* `StandardMaterial` inside this room's own key/fill/
 * ambient lighting rig (`SceneRoot.ts`'s own `frameMaterial`), and an unlit patch sitting on a
 * lit surface will not match it under real lighting — rebuilding the exact "patch stuck onto the
 * beam" look the owner is asking to remove, only harder to notice in a screenshot. So
 * `buildBeamNameTexture`'s caller (`SceneRoot.buildRoom`) builds a *lit* material for this one
 * beam instead, the same construction `frameMaterial` itself uses.
 *
 * **Why that lit material's own `diffuseColor` is white, not `FRAME_COLOR`, even though the
 * texture's background paints `FRAME_COLOR`.** `StandardMaterial` multiplies `diffuseColor` by
 * whatever the diffuse texture samples at each pixel. If both were `FRAME_COLOR` (a near-white
 * ~0.94), the background pixels would come out `FRAME_COLOR * FRAME_COLOR` — a real, if subtle,
 * darkening that would make this one beam read measurably different from its three plain
 * siblings. Painting the texture's own background pixels at the literal `FRAME_COLOR` value and
 * leaving the material's `diffuseColor` at white keeps the multiply a no-op there
 * (`white * FRAME_COLOR = FRAME_COLOR`), while the dark ink pixels still come through as ink
 * (`white * ink = ink`) — the standard "texture is the true colour, diffuseColor is neutral"
 * pairing, chosen deliberately over the alternative once traced through the actual maths rather
 * than assumed to be equivalent.
 *
 * **What is proven here and what is not.** `NullEngine` (every test in this project) never
 * rasterizes, so nothing automated can confirm the beam actually *reads* correctly in a
 * headset — not the UV mapping's real-world appearance, and not how the flat additive emissive
 * `SceneRoot.buildRoom` adds on top of this material interacts with the ink's own contrast under
 * real lighting. What tests here can and do pin: the `faceUV` configuration itself (so the
 * intent survives even though the result is not visible), and that `paintBeamName`'s own canvas
 * drawing is correct in isolation — see `tools/beam-name-preview/render-beam-name-preview.mjs`,
 * which renders the actual canvas output (not a description of it) to a PNG via headless Edge,
 * the same "render one for a human to look at" approach `tools/mic-glyph/` already established
 * (CLAUDE.md, "When nothing in the project can see a rendered pixel...").
 */

/**
 * `CreateBox`'s `faceUV` face order and each face's own outward normal, read directly out of
 * the actual vertex data
 * (`node_modules/@babylonjs/core/Meshes/Builders/boxBuilder.js`, `CreateBoxVertexData`'s own
 * `normals` array, grouped four vertices per face in construction order): index 0 → +Z, index 1
 * → −Z, index 2 → +X, index 3 → −X, index 4 → +Y (top), index 5 → −Y (bottom).
 *
 * The north wall's own top beam (`SceneRoot.buildRoom`'s per-wall loop) is built with no
 * rotation at all — `beam.position.set(...)` is the only transform ever applied to it — so its
 * local axes are the world axes, unchanged. The room-facing side of the north wall is −Z
 * (`WhiteboardLayout.ts`'s own board `normal: new Vector3(0, 0, -1)`, "the board's
 * outward-facing normal into the room" — the same fact the old, now-removed plaque's own module
 * comment already established for this wall). So **face index 1 is the one that needs the
 * text**; every other index has to be steered toward a blank region of the same texture, or
 * `CreateBox`'s own default (every face shows the whole texture, confirmed from the same source:
 * `faceUV[f] === undefined` falls back to `new Vector4(0, 0, 1, 1)` per face) would put the name
 * on all six faces, including the ones pointing away from the room, up at the ceiling, and down
 * into the wall below.
 *
 * **Whether the default identity rectangle on that one face reads left-to-right, not
 * mirrored, for someone standing in the room.** Worked out from the same vertex data, not
 * assumed to carry over from `LabelledPlate.ts`'s own plane — CLAUDE.md's own warning applies
 * here precisely: a plane's front face is −Z by an entirely different mechanism (`CreatePlane`'s
 * own normals), and this box's own UV winding needed checking on its own terms. Tracing
 * `CreateBoxVertexData`'s `uvs.push` calls for face index 1's own four vertices (global vertex
 * indices 4–7, positions `(1,1,-1)`, `(-1,1,-1)`, `(-1,-1,-1)`, `(1,-1,-1)` before the box's own
 * width/height/depth scaling) against a default `faceUV[1] = (0,0,1,1)`: local `x = +1` lands at
 * `u = 1` and local `x = -1` lands at `u = 0` — `u` increases with local (and, since this beam
 * carries no rotation, world) `+X`. Babylon's own axis convention (`Vector3.Right()` is
 * unconditionally `(1,0,0)`; `Vector3.Forward(false)`, the left-handed default this project
 * uses, is `(0,0,1)`) makes `+X` the *right* hand of anyone facing `+Z` with an identity
 * rotation — and a person standing south of the north wall, looking at it, faces `+Z`. So the
 * texture's own left edge (`u=0`) lands at that viewer's left (world `-X`) and the texture's own
 * right edge (`u=1`) lands at their right (world `+X`): text painted in ordinary reading order
 * on the canvas already reads correctly on this face with the identity rectangle, no flip
 * needed — unlike a `CreatePlane`, which needs a yaw correction elsewhere in this project for
 * exactly this reason. This is the "assert the configuration explicitly" half of what can be
 * proven without a rendered pixel; `RoomPlaque.test.ts` pins both this array's own values and
 * the real, constructed mesh's own UV vertex data against it.
 */
const ROOM_FACING_FACE_UV = new Vector4(0, 0, 1, 1);

/**
 * The UV rectangle every *other* face gets — a small square near one corner of the canvas that
 * `paintBeamName`'s own layout (`BEAM_NAME_MAX_TEXT_WIDTH_FRACTION`, comfortably under half the
 * canvas width, always centred) guarantees is nothing but the flat background fill, regardless
 * of how long a given room's name is. Any face mapped here reads as a plain, uniform
 * `FRAME_COLOR` patch — indistinguishable from `frameMaterial`'s own flat colour on the beam's
 * three siblings — rather than a stretched, unreadable crop of the sign.
 */
const BACKGROUND_ONLY_FACE_UV = new Vector4(0, 0, 0.05, 0.05);

/**
 * The full six-element `faceUV` array for the north wall's own top beam — exported so
 * `SceneRoot.buildRoom` can pass it straight to `CreateBox`. Only index 1 (the room-facing face)
 * differs from the rest; see `ROOM_FACING_FACE_UV`'s own doc comment for why that index
 * specifically, and `BACKGROUND_ONLY_FACE_UV`'s for why the other five are not simply left at
 * `CreateBox`'s own default.
 */
export const BEAM_NAME_FACE_UV: Vector4[] = [
  BACKGROUND_ONLY_FACE_UV, // 0: +Z — outward, away from the room
  ROOM_FACING_FACE_UV, // 1: -Z — the room-facing face
  BACKGROUND_ONLY_FACE_UV, // 2: +X
  BACKGROUND_ONLY_FACE_UV, // 3: -X
  BACKGROUND_ONLY_FACE_UV, // 4: +Y — top, against the ceiling
  BACKGROUND_ONLY_FACE_UV, // 5: -Y — bottom, inside the wall
];

/** Fixed vertical resolution for the beam's own name texture — the beam's real height
 *  (`FRAME_MEMBER_THICKNESS_METRES`, 0.18m in the seeded room) is the tightly-constrained
 *  dimension, so this is what actually governs how crisp the lettering reads; width is derived
 *  from it below, off the beam's own real aspect ratio, not fixed independently. */
const BEAM_NAME_TEXTURE_HEIGHT_PX = 48;

/** Defensive bounds on the derived texture width — the one template this project ships
 *  (`DbSeeder.BasicRoomFloorplan`, 8m) derives 2133px, comfortably inside both, but a future
 *  template with a very narrow or very wide wall should neither build a texture too small to
 *  hold legible text nor one large enough to be a real memory concern on a Quest's mobile GPU. */
const BEAM_NAME_TEXTURE_MIN_WIDTH_PX = 256;
const BEAM_NAME_TEXTURE_MAX_WIDTH_PX = 3072;

/** Ink colour for the room name — dark, for contrast against the light `FRAME_COLOR` background
 *  this paints under it (the owner's own request: "white letters really doesn't work... " on a
 *  small black surface — this flips which side is dark). Independent of every other "dark ink"
 *  hex already in this project (e.g. `WhiteboardLayout.ts`'s own pen black) rather than reusing
 *  one of them — nothing ties this specific surface's ink to a stroke's own default colour. */
const BEAM_NAME_INK = '#15181C';

/** Font size as a fraction of the texture's own height — the same 0.62 ratio
 *  `LabelledPlate.ts`'s own `paintMultiLinePlate` already uses for a single row of text, reused
 *  rather than re-derived. */
const BEAM_NAME_FONT_HEIGHT_FRACTION = 0.62;

/** The floor `paintBeamName` will shrink a long name's own font size down to, rather than
 *  truncating it — matches `LabelledPlate.ts`'s own minimum for the identical reason: below this
 *  a name stops being legible regardless of how much further it shrinks. */
const BEAM_NAME_MIN_FONT_PX = 8;

/**
 * The widest a name is ever allowed to paint, as a fraction of the full texture width — well
 * under half, so a centred name always leaves `BACKGROUND_ONLY_FACE_UV`'s own sampled corner
 * genuinely blank no matter how long the room's name is. A long name shrinks its own font to
 * fit this rather than being truncated — see `paintBeamName`'s own body.
 */
const BEAM_NAME_MAX_TEXT_WIDTH_FRACTION = 0.5;

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Paints `name` centred on an otherwise-flat `backgroundHex` canvas — the pixel content of the
 * beam's own name texture, factored out so `buildBeamNameTexture` (fresh texture) and
 * `repaintBeamName` (existing texture, repainted in place) share one drawing routine, the same
 * `paintGridTexture`/`gridTexture` split `SceneRoot.ts` already uses for the floor grid. An
 * empty name paints the background alone — defensive; `RoomDto.name` is not optional on the
 * wire, but nothing here assumes that holds forever.
 */
function paintBeamName(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  name: string,
  backgroundHex: string,
): void {
  context.clearRect(0, 0, width, height);
  context.fillStyle = backgroundHex;
  context.fillRect(0, 0, width, height);

  if (name.length === 0) {
    return;
  }

  context.fillStyle = BEAM_NAME_INK;
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  let fontPx = Math.round(height * BEAM_NAME_FONT_HEIGHT_FRACTION);
  context.font = `bold ${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;

  // Shrink to fit rather than truncate — unlike ErrorLogPanel's own short diagnostic lines
  // (LabelledPlate.ts's own `truncateToWidth`), a room's name is the entire point of this
  // surface, so cutting it short defeats the purpose; there is plenty of spare canvas width to
  // shrink into instead (BEAM_NAME_MAX_TEXT_WIDTH_FRACTION is comfortably under half).
  const maxTextWidth = width * BEAM_NAME_MAX_TEXT_WIDTH_FRACTION;
  const measured = context.measureText(name).width;
  if (measured > maxTextWidth && measured > 0) {
    fontPx = Math.max(BEAM_NAME_MIN_FONT_PX, Math.floor(fontPx * (maxTextWidth / measured)));
    context.font = `bold ${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  }

  context.fillText(name, width / 2, height / 2 + 1);
}

/**
 * Builds a fresh `DynamicTexture` sized off the beam's own real aspect ratio
 * (`beamWidthMetres`/`beamHeightMetres`, the room-facing face's actual physical dimensions) and
 * paints `name` onto it — `SceneRoot.buildRoom` calls this once per room build, the same
 * "fresh texture every rebuild" treatment every other room-shell texture in that file gets
 * (`gridTexture`, the wall-grain textures). `backgroundHex` is threaded in by the caller rather
 * than imported from `SceneRoot.ts` — see this file's own module comment for why the material
 * built around this texture has to know that colour too, and why importing it here instead
 * would create a cycle (`SceneRoot.ts` already imports this function).
 *
 * No mipmaps — matches `gridTexture`'s own choice in `SceneRoot.ts`, not `LabelledPlate.ts`'s
 * own plates (which do generate them). A real, non-`NullEngine` WebGL context rounds a
 * mipmapped texture's dimensions up to the nearest power of two whenever `needPOTTextures` is
 * set (`ThinEngine.createDynamicTexture`, confirmed against the actual source) — invisible on
 * this project's own WebGL2 target in practice, but on `NullEngine` (every test here)
 * `_caps.maxTextureSize` defaults to a mere 512, so a mipmapped version of this unusually wide,
 * elongated texture would clamp every realistic room width down to the same 512px, making it
 * impossible for a test to tell two different room widths apart at all. Confirmed the hard way:
 * a first version of this function requested mipmaps and `RoomPlaque.test.ts`'s own width-scales
 * -with-aspect-ratio assertion failed with both a 4m and an 8m beam landing on an identical
 * 512px texture.
 */
export function buildBeamNameTexture(
  scene: Scene,
  name: string,
  beamWidthMetres: number,
  beamHeightMetres: number,
  backgroundHex: string,
): DynamicTexture {
  const aspect = beamHeightMetres > 0 ? beamWidthMetres / beamHeightMetres : 1;
  const width = clampNumber(
    Math.round(BEAM_NAME_TEXTURE_HEIGHT_PX * aspect),
    BEAM_NAME_TEXTURE_MIN_WIDTH_PX,
    BEAM_NAME_TEXTURE_MAX_WIDTH_PX,
  );

  const texture = new DynamicTexture(
    'whiteboard-frame-name',
    { width, height: BEAM_NAME_TEXTURE_HEIGHT_PX },
    scene,
    false,
  );

  repaintBeamName(texture, name, backgroundHex);

  return texture;
}

/**
 * Repaints an existing beam-name texture in place — a plain `context.fillText` redraw, the same
 * `DynamicTexture.getContext()`/`.update()` shape `SceneRoot.ts`'s own `paintGridTexture` and
 * `LabelledPlate.ts`'s own `repaintMultiLinePlate` already use, at whatever pixel size `texture`
 * was actually built at (`getSize()`, not assumed). No caller in this project needs this today —
 * `SceneRoot.buildRoom` always builds a fresh texture, because it always rebuilds the whole room
 * on every call, resync included — but the mechanism itself has to be a repaint, not a rebuilt
 * mesh, and this is the piece that proves it: unlike the old plaque (a `CreatePlane`, which
 * cannot resize or relabel itself and had to be disposed and rebuilt on every name change), a
 * `DynamicTexture` repaints its own canvas in place, no new mesh, material, or texture involved.
 * `RoomPlaque.test.ts` exercises this directly, against the same texture instance twice.
 */
export function repaintBeamName(texture: DynamicTexture, name: string, backgroundHex: string): void {
  const { width, height } = texture.getSize();
  const context = as2d(texture.getContext());
  paintBeamName(context, width, height, name, backgroundHex);
  texture.update();
}
